import React, { FunctionComponent, useMemo } from 'react';
import { withApiData, ApiDataBinding } from 'react-api-data/lib';
import Select from 'react-select';
import { compose } from 'recompose';
import { WithTranslation, withTranslation } from 'react-i18next';
import { ContractItem } from 'constants/api';
import get from 'lodash/get';
import { NetworkRequestStatus } from '../../constants/literals';
import Spinner from 'reactstrap/lib/Spinner';

interface InProps {
    onChange: (contractItem: ContractItem) => void;
    value: string | null;
    managingContractUuid: string;
    disabled?: boolean;
    exchange?: string;
    erpContracts?: boolean;
}

interface ContractsSelectAllProps {
    allContractsRequest: ApiDataBinding<ContractItem[]>;
    onChange: (contractItem: ContractItem) => void;
    managingContractUuid: string;
    value: string | null;
    disabled?: boolean;
    exchange?: string;
    erpContracts?: boolean;
}

type WrapperProps = InProps & EnhanceProps & WithTranslation;

interface EnhanceProps {
    allContractsRequest: ApiDataBinding<ContractItem[]>;
}

const enhance = compose<WrapperProps, InProps>(
    withApiData({ allContractsRequest: 'getContractsCompact' }, (ownProps) => ({
        allContractsRequest: { ...(ownProps.erpContracts && { erp_contracts: 'true' }) }
    })),
    withTranslation()
);

const ContractsSelectAll: FunctionComponent<ContractsSelectAllProps> = ({
    allContractsRequest,
    onChange,
    managingContractUuid,
    value,
    disabled,
    exchange,
}) => {
    const allContracts = useMemo(() => get(allContractsRequest, 'data.data.contracts', []), [allContractsRequest]);
    const defaultContract = useMemo(
        () => allContracts.find((o: ContractItem) => o.uuid === managingContractUuid),
        [managingContractUuid, allContracts]
    );

    const allowedContracts = exchange
        ? allContracts.filter((contract: ContractItem) => contract.exchange === exchange)
        : allContracts;

    let options = useMemo(
        () =>
            allowedContracts.map((contract: ContractItem) => ({
                label: contract.number + ' -> ' + contract.name,
                value: contract.uuid,
            })),
        [allowedContracts]
    );

    const contractAlreadyExist = options.some((o: any) => o.value === managingContractUuid);
    if (defaultContract && !contractAlreadyExist) {
        options = [
            {
                label: defaultContract.number + ' -> ' + defaultContract.name,
                value: defaultContract.uuid,
            },
            ...options,
        ];
    }

    const optChanged = (e: any) => {
        onChange(allowedContracts.find((contract: ContractItem) => contract.uuid === e.value));
    };

    const selectedOption = useMemo(
        () => options.find((o: { value: string }) => o.value === value),
        [value, options]
    );

    return (
        <Select
            options={options}
            onChange={optChanged}
            isLoading={false}
            defaultValue={selectedOption}
            isDisabled={disabled || false}
        />
    );
};

const ContractsSelectAllWrapper: FunctionComponent<WrapperProps> = ({
    allContractsRequest,
    onChange,
    value,
    managingContractUuid,
    disabled,
    exchange,
    erpContracts
}) => {
    if (allContractsRequest.request.networkStatus === NetworkRequestStatus.Success) {
        return (
            <ContractsSelectAll
                allContractsRequest={allContractsRequest}
                onChange={onChange}
                value={value}
                managingContractUuid={managingContractUuid}
                disabled={disabled}
                exchange={exchange}
                erpContracts={erpContracts}
            />
        );
    } else {
        return <Spinner type="grow" color="primary" />;
    }
};

export default enhance(ContractsSelectAllWrapper);
